import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

type InputMode = 'ptt' | 'vad';

@customElement('cp-call-controls')
export class CpCallControls extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) muted = false;
  @property({ type: String, attribute: 'input-mode' }) inputMode: InputMode = 'vad';
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean, attribute: 'text-mode' }) textMode = false;

  @state() private _pttActive = false;
  @state() private _textInput = '';

  private _boundKeyDown = this._onKeyDown.bind(this);
  private _boundKeyUp = this._onKeyUp.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._boundKeyDown);
    document.addEventListener('keyup', this._boundKeyUp);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundKeyDown);
    document.removeEventListener('keyup', this._boundKeyUp);

    // Release PTT if held when disconnecting
    if (this._pttActive) {
      this._pttActive = false;
      this._dispatchEvent('ptt-end');
    }
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (this.disabled || this.inputMode !== 'ptt') return;
    // Ignore if typing in an input/textarea
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

    if (e.code === 'Space' && !e.repeat) {
      e.preventDefault();
      if (!this._pttActive) {
        this._pttActive = true;
        this._dispatchEvent('ptt-start');
      }
    }
  }

  private _onKeyUp(e: KeyboardEvent): void {
    if (this.disabled || this.inputMode !== 'ptt') return;

    if (e.code === 'Space') {
      e.preventDefault();
      if (this._pttActive) {
        this._pttActive = false;
        this._dispatchEvent('ptt-end');
      }
    }
  }

  private _toggleMute(): void {
    if (this.disabled) return;
    this._dispatchEvent('mute-toggle');
  }

  private _hangUp(): void {
    this._dispatchEvent('hang-up');
  }

  private _toggleInputMode(): void {
    if (this.disabled) return;
    const newMode: InputMode = this.inputMode === 'ptt' ? 'vad' : 'ptt';
    this.dispatchEvent(new CustomEvent('mode-change', {
      detail: { mode: newMode },
      bubbles: true,
      composed: true,
    }));
  }

  private _toggleTextMode(): void {
    this.dispatchEvent(new CustomEvent('text-mode-toggle', {
      bubbles: true,
      composed: true,
    }));
  }

  private _onTextInput(e: InputEvent): void {
    this._textInput = (e.target as HTMLInputElement).value;
  }

  private _onTextKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._sendText();
    }
  }

  private _sendText(): void {
    const text = this._textInput.trim();
    if (!text) return;
    this.dispatchEvent(new CustomEvent('text-send', {
      detail: { text },
      bubbles: true,
      composed: true,
    }));
    this._textInput = '';
    // Clear the input field
    const input = this.querySelector<HTMLInputElement>('.cp-call-controls__text-input');
    if (input) input.value = '';
  }

  private _onPttPointerDown(): void {
    if (this.disabled || this.inputMode !== 'ptt') return;
    if (!this._pttActive) {
      this._pttActive = true;
      this._dispatchEvent('ptt-start');
    }
  }

  private _onPttPointerUp(): void {
    if (this._pttActive) {
      this._pttActive = false;
      this._dispatchEvent('ptt-end');
    }
  }

  private _dispatchEvent(name: string): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    const muteClass = `cp-call-controls__btn cp-call-controls__btn--mute${this.muted ? ' cp-call-controls__btn--muted' : ''}`;
    const pttClass = `cp-call-controls__btn cp-call-controls__btn--ptt${this._pttActive ? ' cp-call-controls__btn--ptt-active' : ''}`;
    const modeClass = `cp-call-controls__btn cp-call-controls__btn--mode`;
    const textToggleClass = `cp-call-controls__btn cp-call-controls__btn--text-toggle${this.textMode ? ' cp-call-controls__btn--text-active' : ''}`;

    return html`
      <div class="cp-call-controls${this.disabled ? ' cp-call-controls--disabled' : ''}">
        ${this.textMode ? html`
          <!-- Text input row (visible in text mode) -->
          <div class="cp-call-controls__text-row">
            <input
              type="text"
              class="cp-call-controls__text-input"
              placeholder="Nachricht eingeben..."
              @input=${this._onTextInput}
              @keydown=${this._onTextKeyDown}
            />
            <button
              class="cp-call-controls__btn cp-call-controls__btn--send"
              @click=${this._sendText}
              ?disabled=${!this._textInput.trim()}
              title="Senden"
            >
              <svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        ` : html`
          <!-- Voice controls (hidden in text mode) -->
          <!-- Input mode toggle (PTT / VAD) -->
          <button
            class="${modeClass}"
            @click=${this._toggleInputMode}
            ?disabled=${this.disabled}
            title="${this.inputMode === 'ptt' ? 'Push-to-Talk aktiv - Klicken fuer VAD' : 'VAD aktiv - Klicken fuer Push-to-Talk'}"
          >
            <span class="cp-call-controls__label">${this.inputMode === 'ptt' ? 'PTT' : 'VAD'}</span>
          </button>

          <!-- Mute toggle -->
          <button
            class="${muteClass}"
            @click=${this._toggleMute}
            ?disabled=${this.disabled}
            title="${this.muted ? 'Mikrofon aktivieren' : 'Mikrofon stummschalten'}"
          >
            ${this.muted
              ? html`<svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.49-.34 2.18"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`
              : html`<svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`
            }
          </button>

          <!-- Push-to-Talk button (visible only in PTT mode) -->
          ${this.inputMode === 'ptt' ? html`
            <button
              class="${pttClass}"
              @pointerdown=${this._onPttPointerDown}
              @pointerup=${this._onPttPointerUp}
              @pointerleave=${this._onPttPointerUp}
              ?disabled=${this.disabled}
              title="Gedrueckt halten zum Sprechen (oder Leertaste)"
            >
              <svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg>
              <span class="cp-call-controls__label">Halten</span>
            </button>
          ` : nothing}
        `}

        <!-- Text/Voice toggle button -->
        <button
          class="${textToggleClass}"
          @click=${this._toggleTextMode}
          title="${this.textMode ? 'Zu Sprache wechseln' : 'Zu Text wechseln'}"
        >
          ${this.textMode
            ? html`<svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`
            : html`<svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>`
          }
        </button>

        <!-- Hang-up button -->
        <button
          class="cp-call-controls__btn cp-call-controls__btn--hangup"
          @click=${this._hangUp}
          title="Auflegen"
        >
          <svg class="cp-call-controls__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" transform="rotate(135 12 12)"/></svg>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-call-controls': CpCallControls;
  }
}
